import React, { useEffect, useState, useRef, useMemo } from"react";
import { useLocation, useNavigate } from "react-router-dom";
import SidebarTreeView from"./SidebarTechTree";
import EliteMarkdownViewer from"./EliteMarkdownViewer";
import FloatingActions from "./FloatingActions";
import ThemeToggle from"./ThemeToggle";
import LockedPopup from"../components/LockedPopup";
// import ChatBot from "../components/SkillGenBot/ChatBot"; // Removed 
import { hasFullAccess, checkPaymentStatus } from "../utils/paymentStatus";
import { useTheme } from "../contexts/ThemeContext";
import { useTenant } from "../Context/TenantContext";
import { 
  Layout, 
  Menu as MenuIcon, 
  Search, 
  Bell, 
  BookOpen, 
  ChevronDown, 
  Clock, 
  ThumbsUp, 
  ThumbsDown, 
  ArrowLeft, 
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Rocket,
  PanelLeftClose,
  PanelLeftOpen
} from "lucide-react";

import PremiumUpgradeModal from "./PremiumUpgradeModal";
import { useSubscription } from "../Context/SubscriptionContext";

const TopicExplorer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const techParam = queryParams.get("tech");

  const [contentMap, setContentMap] = useState([]);
  const [selectedFile, setSelectedFile] = useState("");
  const { theme, isDark } = useTheme();
  const { tenant } = useTenant();
  const contentRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [flatSubtopics, setFlatSubtopics] = useState([]);

  // Flatten subtopics for navigation
  useEffect(() => {
    if (contentMap.length > 0) {
      const flattened = [];
      contentMap.forEach(tech => {
        tech.topics.forEach(topic => {
          topic.subtopics.forEach(sub => {
            flattened.push({
              ...sub,
              techName: tech.technology,
              topicName: topic.topic
            });
          });
        });
      });
      setFlatSubtopics(flattened);
    }
  }, [contentMap]);

  
  const { subscription, canAccessFeature, showPremiumPopup } = useSubscription();
  const isPaid = subscription.isPaid;
  const [isPaidState, setIsPaid] = useState(isPaid);

  useEffect(() => {
    setIsPaid(isPaid);
  }, [isPaid]);

  const [visitedFilesByTopic, setVisitedFilesByTopic] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // 📝 Load saved progress on mount
  useEffect(() => {
    const saved = localStorage.getItem('last_read_doc');
    if (saved) {
      const { file, tech, scrollTop, scrollProgress: savedProgress } = JSON.parse(saved);
      if (tech === techParam || (!techParam && tech === 'AI')) {
        setSelectedFile(file);
        if (savedProgress) setScrollProgress(savedProgress);
        if (scrollTop) {
            setTimeout(() => {
                if (contentRef.current) contentRef.current.scrollTo({ top: scrollTop, behavior: 'instant' });
            }, 200);
        }
      }
    }
  }, [techParam]);

  const currentTopicInfo = useMemo(() => {
    if (!selectedFile || flatSubtopics.length === 0) return null;
    return flatSubtopics.find(s => s.file === selectedFile);
  }, [selectedFile, flatSubtopics]);

  // 📝 Save progress to localStorage
  useEffect(() => {
    if (selectedFile) {
      const existing = JSON.parse(localStorage.getItem('last_read_doc') || "{}");
      const currentScrollTop = contentRef.current ? contentRef.current.scrollTop : 0;
      
      localStorage.setItem('last_read_doc', JSON.stringify({
        ...existing,
        file: selectedFile,
        tech: techParam || currentTopicInfo?.techName || 'AI',
        title: currentTopicInfo?.title || selectedFile.split('/').pop().replace('.md', '').replace(/_/g, ' '),
        scrollProgress: scrollProgress,
        scrollTop: currentScrollTop,
        path: selectedFile,
        timestamp: new Date().toISOString()
      }));
    }
  }, [selectedFile, scrollProgress, techParam, currentTopicInfo]);

  // 📊 Track scroll progress and mark as completed
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        const totalScrollable = scrollHeight - clientHeight;

        let progress = 0;
        if (totalScrollable <= 0) {
          progress = 100;
        } else {
          progress = Math.round((scrollTop / totalScrollable) * 100);
        }
        
        const currentProgress = Math.min(Math.max(progress, 0), 100);
        setScrollProgress(currentProgress);

        // Mark as completed if > 90%
        if (currentProgress > 90 && selectedFile) {
          const tech = techParam || (flatSubtopics.length > 0 ? flatSubtopics.find(s => s.file === selectedFile)?.techName : null) || 'AI';
          const completedKey = `completed_docs_${tech}`;
          const completedDocs = JSON.parse(localStorage.getItem(completedKey) || "[]");
          if (!completedDocs.includes(selectedFile)) {
            completedDocs.push(selectedFile);
            localStorage.setItem(completedKey, JSON.stringify(completedDocs));
            
            // Recalculate and update the technology progress immediately in localStorage
            // to help Dashboard.jsx if it's updated while this tab is open
            const progressKey = `progress_${tech}`;
            localStorage.setItem(progressKey, JSON.stringify({
              completed: completedDocs.length,
              total: flatSubtopics.filter(s => s.techName === tech || (!techParam && s.techName === 'AI')).length,
              timestamp: new Date().toISOString()
            }));
          }

          // Update global activity log
          const activity = JSON.parse(localStorage.getItem('user_activity') || "[]");
          const activityItem = {
            event: `Mastered ${selectedFile.split('/').pop().replace('.md', '').replace(/_/g, ' ')}`,
            tech: tech,
            time: new Date().toISOString(),
            type: 'mastery'
          };
          if (activity.length === 0 || activity[0].event !== activityItem.event) {
            const newActivity = [activityItem, ...activity.filter(a => a.event !== activityItem.event)].slice(0, 10);
            localStorage.setItem('user_activity', JSON.stringify(newActivity));
          }
        }
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      // Initial calculation
      handleScroll();

      contentElement.addEventListener('scroll', handleScroll, { passive: true });
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [selectedFile, techParam, flatSubtopics]);

  // Handle global scroll to top when mounting this page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // 🔐 Check if user is paid
  useEffect(() => {
    (async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user?.userId;

        // Elite content (java/python premium) — always requires payment, no tenant bypass
        if (techParam === "java" || techParam === "python") {
          const selectedSpec = localStorage.getItem("elite_specialization");
          const paymentStatus = await checkPaymentStatus(userId);

          // Redirect to payment if not paid
          if (!paymentStatus.hasPayment || !paymentStatus.isValid) {
            window.location.href = "/skillgenpayments";
            return;
          }

          // Paid but no specialization selected yet → dashboard to pick one
          if (!selectedSpec) {
            window.location.href = "/dashboard";
            return;
          }

          // Specialization mismatch — map java-premium→java, python-premium→python
          const specTech = selectedSpec === 'java-premium' ? 'java' : selectedSpec === 'python-premium' ? 'python' : selectedSpec;
          if (specTech !== techParam) {
            window.location.href = "/dashboard";
            return;
          }

          setIsPaid(true);
          return;
        }

        // Non-elite content: tenant student bypass (free access to AI/AWS/React docs)
        const host = window.location.hostname;
        const isTenantHost = host !== 'theskillgenie.com' && 
                            host !== 'www.theskillgenie.com' && 
                            host !== 'localhost' &&
                            host !== '127.0.0.1';

        if (user && (user.role === 'student' || user.role === 'tenant-student' || user.role === 'admin') && (user.tenant || user.college_id || tenant || isTenantHost)) {
          setIsPaid(true);
          return;
        }

        if (!userId) {
          setIsPaid(false);
          return;
        }

        const userHasFullAccess = await hasFullAccess(userId);
        setIsPaid(userHasFullAccess);
      } catch {
        setIsPaid(false);
      }
    })();
  }, [tenant]);

 // 📂 Load content map
 useEffect(() => {
   if (techParam === "java" || techParam === "python") {
     const configUrl = techParam === "java" 
       ? "https://raw.githubusercontent.com/gowthamtheskillgenie/skillgenie-curriculum/main/java.json" 
       : "https://raw.githubusercontent.com/gowthamtheskillgenie/skillgenie-curriculum/main/python.json";

     // Using local fallbacks if available, but for now we'll try to fetch or use a local static version if we can
     // Given the environment, I'll assume we can try to fetch them or better, I'll define a local way to get them.
     // Since I can't easily add files to the public folder that will be served, I will check if they are already there.
     
     const localUrl = techParam === "java" ? "/java.json" : "/python.json";
     
     fetch(localUrl)
       .then(res => res.json())
       .then(data => {
         const transformed = data.domains.map(domain => ({
           technology: techParam === "java" ? "Java Elite" : "Python Elite",
           topics: [{
             topic: domain.category,
             subtopics: domain.topics.map(topic => {
               const categoryDir = domain.category.replace(/[^a-zA-Z0-9]/g, '_');
               const fileName = topic.replace(/[^a-zA-Z0-9]/g, '_') + '.md';
               const basePath = techParam === "java" ? "docs/java-premium" : "docs/python-premium";
               return {
                 title: topic,
                 file: `${basePath}/${categoryDir}/${fileName}`
               };
             })
           }]
         }));
         setContentMap(transformed);
       })
       .catch(err => {
         console.error(`❌ Failed to load ${techParam} curriculum`, err);
         // Fallback to main content map if premium fails
         fetch("/contentMap.json")
           .then((res) => res.json())
           .then((data) => setContentMap(data));
       });
   } else {
     const ALLOWED_TECHNOLOGIES = ['AI', 'AWS', 'React'];
     fetch("/contentMap.json")
       .then((res) => res.json())
       .then((data) => setContentMap(data.filter(t => ALLOWED_TECHNOLOGIES.includes(t.technology))))
       .catch((err) => console.error("❌ Failed to load contentMap.json", err));
   }
 }, [techParam]);

  // 🔒 Only allow first 2 subtopics per topic
  const handleSelect = (filePath, isLocked = false) => {
    if (!filePath) return;

    if (isLocked) {
      setShowPopup(true);
      return;
    }

    // Normalize path for comparison (ensure leading slash)
    const normalizedPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
    setSelectedFile(normalizedPath);

    // --- NEW: Scroll main area to top when topic changes ---
    if (contentRef.current) {
        contentRef.current.scrollTop = 0;
    }
  };

  const handleNext = () => {
    const nextIndex = flatSubtopics.findIndex(s => s.file === selectedFile);
    if (nextIndex !== -1 && nextIndex < flatSubtopics.length - 1) {
      handleSelect(flatSubtopics[nextIndex + 1].file);
    }
  };

  const handlePrevious = () => {
    const prevIndex = flatSubtopics.findIndex(s => s.file === selectedFile);
    if (prevIndex > 0) {
      handleSelect(flatSubtopics[prevIndex - 1].file);
    }
  };

  const currentIndex = flatSubtopics.findIndex(s => s.file === selectedFile);
  const hasNext = currentIndex !== -1 && currentIndex < flatSubtopics.length - 1;
  const hasPrevious = currentIndex > 0;

  return (
    <div className={`${isDark ? "bg-[#0B0F1A]" : "bg-[#F8FAFC]"} h-screen flex flex-col font-sans selection:bg-primary/30 overflow-hidden`}>
      {/* 🧭 Top Navigation Bar */}
      <header className={`h-16 flex items-center justify-between px-6 flex-shrink-0 z-[70] border-b backdrop-blur-md ${
        isDark ? "bg-[#0B0F1A]/80 border-white/5" : "bg-white/80 border-gray-200"
      }`}>
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className={`p-2 rounded-lg transition-all ${
              isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-gray-600"
            }`}
            title="Go Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
              <BookOpen className="text-white" size={18} />
            </div>
            <span className={`text-lg font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
              {techParam === 'java' ? 'Java Elite Mastery' : techParam === 'python' ? 'Python Elite Mastery' : 'The Skill Genie'}
            </span>
          </div>
          <div className={`h-6 w-px mx-2 ${isDark ? "bg-white/10" : "bg-gray-200"}`} />
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
            isDark ? "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10" : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
          }`}>
            <span>{currentTopicInfo?.techName || "Select Topic"}</span>
            <ChevronDown size={14} className="opacity-50" />
          </button>
        </div>

        <div className="flex-1 max-w-2xl mx-12 hidden md:block">
          <div className={`relative group flex items-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            <Search className="absolute left-3 group-focus-within:text-primary transition-colors" size={18} />
            <input 
              type="text" 
              placeholder="Search documentation..." 
              className={`w-full bg-transparent border rounded-xl pl-10 pr-16 py-2 text-sm outline-none transition-all ${
                isDark 
                ? "border-white/10 focus:border-primary/50 focus:bg-white/5" 
                : "border-gray-200 focus:border-primary/50 focus:bg-gray-50"
              }`}
            />
            <div className={`absolute right-3 px-1.5 py-0.5 rounded border text-[10px] font-mono ${
              isDark ? "border-white/10 text-gray-500" : "border-gray-200 text-gray-400"
            }`}>
              Ctrl K
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <ThemeToggle />
        </div>
      </header>

      <div className="flex flex-1 min-h-0 relative">
        {/* 📚 Sidebar */}
        <aside className={`
          ${isSidebarOpen ? "w-72" : "w-0"} 
          border-r flex-shrink-0 flex flex-col overflow-hidden
          transition-all duration-300 ease-in-out
          ${isDark ? "bg-[#0B0F1A]" : "bg-[#5B21B6] border-r-0"}
        `}>
          <div className={`p-4 flex items-center justify-between flex-shrink-0 border-b ${isDark ? "border-white/5" : "border-white/10"}`}>
            <div className="flex items-center gap-2">
              <div className="uppercase text-[10px] font-black tracking-[0.2em] text-white/50 whitespace-nowrap">
                Technology Documents
              </div>
            </div>
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`p-1.5 rounded-lg transition-all ${
                isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-white/10 text-white"
              }`}
              title="Collapse Sidebar"
            >
              <PanelLeftClose size={16} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar">
            <SidebarTreeView
              data={contentMap}
              onSelectSubtopic={(file, isLocked) => handleSelect(file, isLocked)}
              theme={theme}
              selectedFile={selectedFile}
              isPaid={isPaid}
              isPurple={!isDark}
            />
          </div>
        </aside>

        {/* 📄 Main Content Area */}
        <main 
          ref={contentRef} 
          id="mastery-content-area"
          className="flex-1 overflow-y-auto relative scroll-smooth bg-transparent"
        >
          {!isSidebarOpen && (
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className={`fixed top-20 left-4 z-50 p-2.5 rounded-xl shadow-2xl border transition-all hover:scale-110 active:scale-95 ${
                isDark ? "bg-[#0B0F1A] border-white/10 text-[#5B21B6]" : "bg-white border-purple-100 text-[#5B21B6]"
              }`}
              title="Expand Sidebar"
            >
              <ChevronRight size={22} />
            </button>
          )}

          {selectedFile ? (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
              {/* Top Navigation sticky bar */}
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-purple-50 sticky top-0 z-50 bg-inherit backdrop-blur-md">
                  <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-purple-50 flex items-center justify-center text-[#5B21B6]">
                          <BookOpen size={20} />
                      </div>
                      <div>
                          <h2 className="text-sm font-black text-[#5B21B6] uppercase tracking-widest">{techParam?.toUpperCase() || 'AI'}</h2>
                          <p className="text-[10px] font-bold text-gray-400 uppercase">Knowledge Explorer</p>
                      </div>
                  </div>

                  <div className="flex items-center gap-4 bg-white/80 backdrop-blur-md p-1.5 rounded-2xl border border-purple-100 shadow-sm">
                      <button 
                          onClick={handlePrevious}
                          disabled={!hasPrevious}
                          className="p-2.5 rounded-xl hover:bg-purple-100 text-[#5B21B6] transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Previous Topic"
                      >
                          <ChevronLeft size={20} />
                      </button>
                      <div className="h-6 w-px bg-purple-100"></div>
                      <button 
                          onClick={handleNext}
                          disabled={!hasNext}
                          className="p-2.5 rounded-xl hover:bg-purple-100 text-[#5B21B6] transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Next Topic"
                      >
                          <ChevronRight size={20} />
                      </button>
                  </div>
              </div>

              {/* Breadcrumb / Top Info (Hidden since we have the new nav) */}
              <div className="hidden flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4 text-[13px]">
                <div className="flex flex-wrap items-center gap-3 text-gray-500">
                  <span className="opacity-60">You're reading</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`font-semibold ${isDark ? "text-gray-300" : "text-slate-700"}`}>
                      {currentTopicInfo?.topicName || "Topic"}
                    </span>
                    <span className="opacity-30">•</span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full border-2 border-primary/30 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      </div>
                      <span>{scrollProgress}% complete</span>
                    </div>
                  </div>
                </div>
                
              </div>

              <EliteMarkdownViewer
                filePath={selectedFile}
              />
              {/* Navigation buttons at bottom removed or integrated into top */}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center max-w-lg mx-auto p-10">
              <div className="w-24 h-24 rounded-3xl bg-primary/10 flex items-center justify-center mb-8 border border-primary/20 shadow-inner">
                <span className="text-5xl animate-bounce-slow">🚀</span>
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>Documentation Library</h3>
              <p className="text-slate-500 text-lg leading-relaxed">
                Select a topic from the sidebar to start learning. Master industry-standard engineering patterns.
              </p>
            </div>
          )}
        </main>
      </div>

      {/* ChatBot removed */}

      {/* 🔒 Locked Access Modal */}
      <LockedPopup show={showPopup} onClose={() => setShowPopup(false)} />

      {/* 🛠 Floating Actions (Notes, Compiler, AI Assistant) */}
      <FloatingActions content={currentTopicInfo?.title || "Technology documentation"} />
    </div>
  );
};

export default TopicExplorer;



